import axios from 'axios'
import store from '@/state/store'

// Token validation cache to avoid checking on every request
let tokenValidationCache = {
    lastCheck: null,
    isValid: false,
    checkInterval: 5 * 60 * 1000 // 5 minutes
}

// create an axios instance
const service = axios.create({
    baseURL: process.env.VUE_APP_API_BASE_URL || 'http://localhost:8000', // url = base url + request url
    // withCredentials: true, // send cookies when cross-domain requests
    timeout: 30000 // request timeout
})

// request interceptor to add token to headers
service.interceptors.request.use(
    async (config) => {
        const user = JSON.parse(localStorage.getItem('user') || '{}')
        
        // Skip token validation and injection for auth endpoints
        if (config.url.includes('/auth/login') || 
            config.url.includes('/auth/register') || 
            config.url.includes('/auth/check-token')) {
            return config
        }
        
        if (user.access_token) {
            // Check if we need to validate token
            const now = Date.now()
            const shouldValidateToken = !tokenValidationCache.lastCheck || 
                                      (now - tokenValidationCache.lastCheck) > tokenValidationCache.checkInterval ||
                                      !tokenValidationCache.isValid
            
            // Validate token periodically
            if (shouldValidateToken) {
                try {
                    // Create a simple request to check token without interceptors to avoid infinite loop
                    const tokenCheckResponse = await axios.get(`${config.baseURL}/auth/check-token`, {
                        headers: {
                            'Authorization': `Bearer ${user.access_token}`,
                            'Content-Type': 'application/json'
                        }
                    })
                    
                    tokenValidationCache.lastCheck = now
                    tokenValidationCache.isValid = tokenCheckResponse.data.valid
                    
                    if (!tokenCheckResponse.data.valid) {
                        // Token is invalid, remove from localStorage and redirect
                        localStorage.removeItem('user')
                        tokenValidationCache.isValid = false
                        
                        // Show alert for token expiration
                        store.dispatch('notification/showError', 'Sua sessão expirou. Faça login novamente.')
                        
                        setTimeout(() => {
                            window.location.href = '/login'
                        }, 2000)
                        
                        return Promise.reject(new Error('Token inválido'))
                    }
                } catch (error) {
                    // Token validation failed, remove from localStorage and redirect
                    localStorage.removeItem('user')
                    tokenValidationCache.isValid = false
                    
                    // Show alert for connection error
                    store.dispatch('notification/showError', 'Falha na validação do token. Faça login novamente.')
                    
                    setTimeout(() => {
                        window.location.href = '/login'
                    }, 2000)
                    
                    return Promise.reject(new Error('Falha na validação do token'))
                }
            }
            
            config.headers.Authorization = `Bearer ${user.access_token}`
        }
        return config
    },
    (error) => {
        return Promise.reject(error)
    }
)
  
// response interceptor
service.interceptors.response.use(
    /**
     * If you want to get http information such as headers or status
     * Please return  response => response
    */

    /**
     * Determine the request status by custom code
     * Here is just an example
     * You can also judge the status by HTTP Status Code
     */
    response => {
        const res = response

        // Accept all successful status codes (200-299)
        if (res.status >= 200 && res.status < 300) {
            return res
        } else {
            console.info(res.data?.message || 'Error response')
            
            // 50008: Illegal token; 50012: Other clients logged in; 50014: Token expired;
            if (res.status === 50008 || res.status === 50012 || res.status === 50014) {
                console.info("50008: Illegal token; 50012: Other clients logged in; 50014: Token expired")            
            }
            return Promise.reject(new Error(res.data?.message || res.statusText || 'Error'))
        }
    },
    error => {
        console.log('err' + error)
        
        // Handle authentication errors
        if (error.response?.status === 401) {
            // Don't redirect if it's a login attempt
            if (!error.config?.url?.includes('/auth/login')) {
                // Token expired or invalid, logout user
                localStorage.removeItem('user')
                tokenValidationCache.isValid = false
                
                // Show alert for unauthorized access
                store.dispatch('notification/showError', 'Acesso não autorizado. Faça login novamente.')
                
                setTimeout(() => {
                    window.location.href = '/login'
                }, 2000)
            }
        } else {
            // Show generic error alert for other types of errors
            let errorMessage = 'Erro de conexão com o servidor'
            
            if (error.response?.data?.detail) {
                errorMessage = error.response.data.detail
            } else if (error.response?.data?.message) {
                errorMessage = error.response.data.message
            } else if (error.message) {
                errorMessage = error.message
            }
            
            // Only show network errors and server errors, not validation errors
            if (error.response?.status >= 500 || !error.response) {
                store.dispatch('notification/showError', errorMessage)
            }
        }
        
        return Promise.reject(error)
    }
)
  
export default service